import { useAuthStore } from '@/store/authStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// 获取认证 header
function getAuthHeader(): HeadersInit {
  const token = useAuthStore.getState().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// 获取评估实例列表
export async function getEvaluationInstances() {
  const response = await fetch(`${API_BASE_URL}/api/evaluation/instances`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || '获取评估实例失败');
  }
  
  return response.json();
}

// 获取评估实例详情
export async function getEvaluationInstance(instanceId: string) {
  const response = await fetch(`${API_BASE_URL}/api/evaluation/instances/${instanceId}`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || '获取评估实例详情失败');
  }
  
  return response.json();
}

// 获取评估实例配置
export async function getEvaluationInstanceConfig(instanceId: string) {
  const response = await fetch(`${API_BASE_URL}/api/evaluation/instances/${instanceId}/config`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || '获取配置失败');
  }
  
  return response.json();
}

// 创建评估任务
export async function createEvaluationTask(instanceId: string) {
  const response = await fetch(`${API_BASE_URL}/api/evaluation/tasks`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
    body: JSON.stringify({ instanceId }),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || '创建评估任务失败');
  }
  
  return response.json();
}

// 获取评估任务详情
export async function getEvaluationTask(taskId: string) {
  const response = await fetch(`${API_BASE_URL}/api/evaluation/tasks/${taskId}`, {
    headers: {
      ...getAuthHeader(),
    },
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || '获取任务详情失败');
  }
  
  return response.json();
}

// 获取用户的所有评估任务
export async function getUserEvaluationTasks() {
  const response = await fetch(`${API_BASE_URL}/api/evaluation/tasks`, {
    headers: {
      ...getAuthHeader(),
    },
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || '获取任务列表失败');
  }
  
  return response.json();
}

// 获取任务的歌曲对列表
export async function getTaskSongPairs(taskId: string) {
  const response = await fetch(`${API_BASE_URL}/api/evaluation/tasks/${taskId}/songs`, {
    headers: {
      ...getAuthHeader(),
    },
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || '获取歌曲列表失败');
  }
  
  return response.json();
}

// 获取歌曲对的混音版本
export async function getSongPairVersions(taskId: string, songPairId: string) {
  const response = await fetch(
    `${API_BASE_URL}/api/evaluation/tasks/${taskId}/songs/${songPairId}/versions`,
    {
      headers: {
        ...getAuthHeader(),
      },
    }
  );
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || '获取混音版本失败');
  }
  
  return response.json();
}

// 提交歌曲熟悉度
export async function submitFamiliarity(
  taskId: string,
  data: {
    songPairId: string;
    trackPrevId: string;
    trackNextId: string;
    isFamiliar: boolean;
    updatedByUser: boolean;
  }
) {
  const response = await fetch(`${API_BASE_URL}/api/evaluation/tasks/${taskId}/familiarity`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
    body: JSON.stringify(data),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || '提交熟悉度失败');
  }
  
  return response.json();
}

// 提交评分
export async function submitRatings(
  taskId: string,
  ratings: Array<{
    songPairId: string;
    versionId: string;
    dimension: string;
    score: number;
  }>
) {
  const response = await fetch(`${API_BASE_URL}/api/evaluation/tasks/${taskId}/ratings`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
    body: JSON.stringify({ ratings }),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || '提交评分失败');
  }
  
  return response.json();
}

// 完成任务
export async function completeTask(taskId: string) {
  const response = await fetch(`${API_BASE_URL}/api/evaluation/tasks/${taskId}/complete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || '完成任务失败');
  }
  
  return response.json();
}

// 获取任务的评分记录
export async function getTaskRatings(taskId: string) {
  const response = await fetch(`${API_BASE_URL}/api/evaluation/tasks/${taskId}/ratings`, {
    headers: {
      ...getAuthHeader(),
    },
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || '获取评分记录失败');
  }
  
  return response.json();
}

